// Miniaturas y ondas de audio del Estudio, guardadas — ADR-2026-11-03-el-estudio-abre-rapido.
//
// La tira de miniaturas de un video son entre 12 y 96 seeks seguidos sobre la red, y la
// onda de un audio es bajar y decodificar el archivo ENTERO. Las dos se calculaban de
// nuevo cada vez que se abría el proyecto (vivían en el estado del editor) y por cada
// clip, aunque dos clips fueran el mismo archivo (las mitades de un clip dividido). Pero
// sólo dependen del ARCHIVO: se guardan por `storagePath`.
//   • en memoria (sobrevive a salir del editor y volver a entrar, dentro de la app);
//   • en IndexedDB (sobrevive a recargar). Si no hay IndexedDB (modo privado,
//     bloqueado), queda la memoria y nada se rompe.
// Topes: 80 entradas en memoria y 200 en IndexedDB (las más viejas se van).

const MAX_EN_MEMORIA = 80;
const MAX_EN_DISCO = 200;
const memoria = new Map<string, unknown>();

function recordar(clave: string, valor: unknown) {
  memoria.delete(clave);
  memoria.set(clave, valor);
  while (memoria.size > MAX_EN_MEMORIA) {
    const vieja = memoria.keys().next().value;
    if (vieja === undefined) break;
    memoria.delete(vieja);
  }
}

let base: Promise<IDBDatabase | null> | null = null;
function abrirBase(): Promise<IDBDatabase | null> {
  if (base) return base;
  base = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const pedido = indexedDB.open('atlas-estudio-medios', 1);
      pedido.onupgradeneeded = () => {
        pedido.result.createObjectStore('medios');
      };
      pedido.onsuccess = () => resolve(pedido.result);
      pedido.onerror = () => resolve(null);
      pedido.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return base;
}

type Guardado = { v: unknown; t: number };

export async function leerDeLaCache<T>(clave: string): Promise<T | null> {
  if (memoria.has(clave)) {
    const v = memoria.get(clave) as T;
    recordar(clave, v);
    return v;
  }
  const db = await abrirBase();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const pedido = db.transaction('medios', 'readonly').objectStore('medios').get(clave);
      pedido.onsuccess = () => {
        const g = pedido.result as Guardado | undefined;
        if (!g) return resolve(null);
        recordar(clave, g.v);
        resolve(g.v as T);
      };
      pedido.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

let guardadosDesdeLaPoda = 0;
export function guardarEnLaCache(clave: string, valor: unknown): void {
  recordar(clave, valor);
  void abrirBase().then((db) => {
    if (!db) return;
    try {
      db.transaction('medios', 'readwrite')
        .objectStore('medios')
        .put({ v: valor, t: Date.now() } satisfies Guardado, clave);
    } catch {
      return;
    }
    // Cada tanto se poda: lo más viejo se va para que la base no crezca sin fin.
    guardadosDesdeLaPoda += 1;
    if (guardadosDesdeLaPoda >= 20) {
      guardadosDesdeLaPoda = 0;
      podar(db);
    }
  });
}

function podar(db: IDBDatabase) {
  try {
    const store = db.transaction('medios', 'readwrite').objectStore('medios');
    const entradas: { clave: IDBValidKey; t: number }[] = [];
    const cursor = store.openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (c) {
        entradas.push({ clave: c.key, t: (c.value as Guardado)?.t ?? 0 });
        c.continue();
        return;
      }
      if (entradas.length <= MAX_EN_DISCO) return;
      entradas.sort((a, b) => a.t - b.t);
      for (const e of entradas.slice(0, entradas.length - MAX_EN_DISCO)) store.delete(e.clave);
    };
  } catch {
    // sin poda esta vez
  }
}

export const claveDeMiniaturas = (storagePath: string, cuantas: number) =>
  `mini:${storagePath}:${cuantas}`;
export const claveDeOnda = (storagePath: string) => `onda:${storagePath}`;

/**
 * Corre tareas de a `maximo` por vez. Las tiras de miniaturas son un `<video>` oculto
 * cada una: abrir un proyecto con 10 videos arrancaba 10 descargas y 10 decodificadores
 * a la vez, peleándose con el preview.
 */
export function crearLimitador(maximo: number) {
  let corriendo = 0;
  const cola: (() => void)[] = [];
  const siguiente = () => {
    if (corriendo >= maximo) return;
    const tarea = cola.shift();
    if (tarea) tarea();
  };
  return function <T>(trabajo: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      cola.push(() => {
        corriendo += 1;
        trabajo()
          .then(resolve, reject)
          .finally(() => {
            corriendo -= 1;
            siguiente();
          });
      });
      siguiente();
    });
  };
}

/** Si ya hay un cálculo en vuelo para esa clave, se espera ése en vez de arrancar otro. */
const enVuelo = new Map<string, Promise<unknown>>();
export function unaVezPorClave<T>(clave: string, crear: () => Promise<T>): Promise<T> {
  const ya = enVuelo.get(clave) as Promise<T> | undefined;
  if (ya) return ya;
  const p = crear().finally(() => enVuelo.delete(clave));
  enVuelo.set(clave, p);
  return p;
}
